// 아바타 몸 염색 부위 마스크(docs/AVATAR-ADVENTURE-PRD.md 5절 2차).
// 옷(천·가죽·갑옷 바탕)만 물들고 피부·머리카락·금속 장식은 그대로: public/assets/characters/<몸>.dyemask.webp(1채널, 512², 몸 GLB와 같은 UV)를
// 재질 colorNode에서 읽어 염료색을 마스크가 1인 곳에만 곱한다. 기존 color·map·colorNode는 그대로 쓴다(materialColor가 color×map).
// 마스크가 없으면(파일 없음·로드 실패·노드 재질 아님) 예전 방식(재질 color 전체에 약하게 곱함)으로 떨어진다.
//
// 재질 공유: 영웅 몸 재질은 KaelModel마다 따로 만든다(createKaelSurface, GLB를 읽을 때마다 새 GLTF). 용병(heroModels·mercs)은 다른 GLB·재질이라
// 번지지 않는다. 재질을 복제하면 KaelModel이 쥔 참조(피격 emissive·단계 교체)가 끊기므로 복제하지 않고 같은 재질을 제자리에서 고친 뒤
// clearDye로 되돌린다(원래 colorNode·color를 WeakMap에 보관). 그러므로 호출 쪽은 재질을 바꾸거나 버리기 전에 clearDye를 부른다.
// 마스크 텍스처는 모듈 캐시가 쥔다 — dispose하지 않는다(몸이 바뀌어도 다시 쓴다).
import * as THREE from 'three/webgpu';
import { materialColor, mix, texture, uniform, uv, vec3 } from 'three/tsl';
import { assetUrl } from '../pwa/assetMap';

/** 마스크가 있는 몸(= 아바타 외형이 붙는 영웅 몸). 카엘은 단계(T1~T3)마다 UV가 달라 마스크도 따로다 */
export const DYE_MASK_IDS = ['kael_t1', 'kael_t2', 'kael_t3', 'lyra_t1'] as const;
export type DyeMaskId = (typeof DYE_MASK_IDS)[number];

/** 마스크가 있을 때의 염색 세기(옷만 물들므로 높게) / 없을 때 전체 곱의 세기(피부까지 물들어 낮게) */
export const DYE_STRENGTH_MASKED = 0.8;
export const DYE_STRENGTH_FALLBACK = 0.45;

/** 영웅·방어구 단계 → 마스크 이름. 모르는 영웅·단계는 null(마스크 없음) */
export function dyeMaskId(heroId: string, tier = 1): DyeMaskId | null {
  if (heroId === 'lyra') return 'lyra_t1';
  if (heroId === 'kael') return tier === 2 ? 'kael_t2' : tier === 3 ? 'kael_t3' : 'kael_t1';
  return null;
}

const SHIPPED: ReadonlySet<string> = new Set(DYE_MASK_IDS);
export type DyeMaskFetcher = (url: string) => Promise<THREE.Texture>;

/** GLTFLoader와 같은 규칙: 방향 뒤집지 않고(flipY=false) 알파 곱·색 변환 없이 읽는다 */
const defaultFetcher: DyeMaskFetcher = async (url) => {
  const loader = new THREE.ImageBitmapLoader().setOptions({ imageOrientation: 'none', premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const bitmap = await loader.loadAsync(url);
  const tex = new THREE.Texture(bitmap);
  tex.flipY = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
};
let fetcher: DyeMaskFetcher = defaultFetcher;
const cache = new Map<string, Promise<THREE.Texture | null>>();

/** 시험용: 로더를 바꾸고 캐시를 비운다. 인자 없으면 기본 로더로 되돌린다 */
export function setDyeMaskFetcher(f?: DyeMaskFetcher): void { fetcher = f ?? defaultFetcher; cache.clear(); }

/**
 * 마스크를 한 번만 읽는다(같은 id는 같은 약속을 돌려준다). 없는 id·읽기 실패·이 환경에 로더가 없으면 null이고 그 결과도 캐시한다.
 * heroId에 'kael'·'lyra'를 주면 T1 마스크, 단계 몸은 dyeMaskId(heroId, tier)의 결과를 넘긴다.
 */
export function loadDyeMask(heroId: string): Promise<THREE.Texture | null> {
  const id = SHIPPED.has(heroId) ? heroId : dyeMaskId(heroId);
  if (!id) return Promise.resolve(null);
  let p = cache.get(id);
  if (!p) {
    p = fetcher(assetUrl(`characters/${id}.dyemask.webp`)).then((t) => t, (err: unknown) => {
      console.warn(`염색 마스크를 읽지 못해 전체 염색으로 둔다: ${id}`, err);
      return null;
    });
    cache.set(id, p);
  }
  return p;
}

type NodeMat = THREE.Material & { color?: THREE.Color; colorNode?: unknown };
interface DyeState {
  mask: THREE.Texture | null;
  prevColorNode: unknown;
  prevColor: THREE.Color | null;
  color: { value: THREE.Color } | null;
  strength: { value: number } | null;
  node: unknown;
}
const states = new WeakMap<THREE.Material, DyeState>();

/** 염색 중인가(시험·점검용) */
export function isDyed(material: THREE.Material): boolean { return states.has(material); }

/**
 * 염색을 건다. mask가 있고 재질이 노드 재질이면 마스크가 1인 곳에만 mix(1, 염료, strength×마스크)를 곱한다.
 * 이미 같은 마스크로 걸려 있으면 노드를 다시 만들지 않고 uniform만 바꾼다(재컴파일 없음). 마스크가 바뀌면 되돌린 뒤 다시 건다.
 * mask가 null이면 예전 방식: 재질 color 전체에 mix(1, 염료, strength)를 곱한다.
 */
export function applyDyeMasked(material: THREE.Material, mask: THREE.Texture | null, dyeColor: THREE.Color, strength: number): void {
  const m = material as NodeMat;
  const useNode = !!mask && 'colorNode' in m;
  const old = states.get(material);
  if (old && (old.mask !== (useNode ? mask : null))) clearDye(material);
  let st = states.get(material);
  if (!st) {
    st = { mask: useNode ? mask : null, prevColorNode: m.colorNode ?? null, prevColor: m.color ? m.color.clone() : null, color: null, strength: null, node: null };
    if (useNode) {
      const colU = uniform(new THREE.Color()), strU = uniform(0);
      st.color = colU as unknown as { value: THREE.Color }; st.strength = strU as unknown as { value: number };
      const base = (st.prevColorNode ?? materialColor) as typeof materialColor;
      const m1 = texture(mask!, uv()).r.mul(strU);
      st.node = base.mul(mix(vec3(1, 1, 1), colU, m1));
      m.colorNode = st.node;
      material.needsUpdate = true;
    }
    states.set(material, st);
  }
  if (st.color && st.strength) {
    st.color.value.copy(dyeColor);
    st.strength.value = strength;
  } else if (m.color && st.prevColor) {
    m.color.copy(st.prevColor).multiply(new THREE.Color(1, 1, 1).lerp(dyeColor, strength));
  }
}

/** 염색을 걷어 재질을 원래대로(원래 colorNode·color) 되돌린다. 걸려 있지 않으면 아무것도 안 한다 */
export function clearDye(material: THREE.Material): void {
  const st = states.get(material);
  if (!st) return;
  const m = material as NodeMat;
  // 그사이 다른 외형(라이라 색조 등)이 colorNode를 바꿨으면 그쪽 값을 지킨다 — 옛 노드로 덮어쓰지 않는다
  if (st.node) { if (m.colorNode === st.node) { m.colorNode = st.prevColorNode; material.needsUpdate = true; } }
  else if (m.color && st.prevColor) m.color.copy(st.prevColor);
  states.delete(material);
}
